import * as yup from "yup";

const loginSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
});

const createTicketSchema = yup.object({
  title: yup.string().required("Title is required"),
  message: yup.string().required("Message is required"),
  selectedUser: yup.object().nullable().required("Select a user"),
});

const emptyStringToUndefined = (value: any, originalValue: any) =>
  originalValue === "" || originalValue === null ? undefined : value;

const couponCodeField = yup
  .string()
  .trim()
  .required("Coupon code is required")
  .min(5, "Coupon code must be at least 5 characters");

const couponTypeField = yup
  .number()
  .transform(emptyStringToUndefined)
  .required("Coupon type is required")
  .oneOf([1, 2], "Select a valid coupon type");

const couponDiscountPercentageField = yup
  .number()
  .transform(emptyStringToUndefined)
  .when("type", {
    is: 1,
    then: (schema) =>
      schema
        .typeError("Discount percentage must be a number")
        .required("Discount percentage is required")
        .min(1, "Discount percentage must be at least 1%")
        .max(100, "Discount percentage cannot exceed 100%"),
    otherwise: (schema) => schema.notRequired(),
  });

const couponFlatSpmpField = yup
  .number()
  .transform(emptyStringToUndefined)
  .when("type", {
    is: 2,
    then: (schema) =>
      schema
        .typeError("Flat student per month price must be a number")
        .required("Flat student per month price is required")
        .min(1, "Flat student per month price must be at least 1"),
    otherwise: (schema) => schema.notRequired(),
  });

const couponQuantityField = yup
  .number()
  .transform(emptyStringToUndefined)
  .typeError("Quantity must be a number")
  .required("Quantity is required")
  .min(1, "Quantity must be at least 1");

const createCouponSchema = yup.object({
  code: couponCodeField,
  type: couponTypeField,
  discount_percentage: couponDiscountPercentageField,
  flat_spmp: couponFlatSpmpField,
  quantity: couponQuantityField,
  expiry_date: yup
    .date()
    .required("Expiry date is required")
    .min(new Date(), "Expiry date must be in the future"),
});

const updateCouponSchema = yup.object({
  code: couponCodeField,
  type: couponTypeField,
  discount_percentage: couponDiscountPercentageField,
  flat_spmp: couponFlatSpmpField,
  quantity: couponQuantityField,
  expiry_date: yup.date().required("Expiry date is required"),
});

const createBookReviewerSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  contact: yup
    .string()
    .required("Contact is required")
    .matches(/^\d+$/, "Contact must contain only numbers"),
  genres: yup
    .array()
    .min(1, "At least one genre is required")
    .required("Genres are required"),
});

const editProfileSchema = yup.object({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required."),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: yup
    .string()
    .matches(/^\d+$/, "Contact must contain only numbers")
    .required("Contact no. is required."),
});

const changePasswordSchema = yup.object().shape({
  current_password: yup.string().required("Current Password is Required"),
  password: yup.string().required("Password is Required"),
  password_confirmation: yup
    .string()
    .required("Confirm Password is Required")
    .oneOf([yup.ref("password"), ""], "Passwords must match"),
});

const getEmailSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
});

const MOUSchema = yup.object({
  description: yup.string().required("Description is required"),
  freeBooksLimit: yup
    .number()
    .typeError("Free Books Limit must be a number")
    .required("Free Books Limit is required"),
  premiumBooksLimit: yup
    .number()
    .typeError("Premium Books Limit must be a number")
    .required("Premium Books Limit is required"),
  platformFee: yup
    .number()
    .typeError("Platform Fee must be a number")
    .required("Platform Fee is required"),
});

const mouSignatureSchema = yup.object({
  description: yup.string().required("Description is required"),
  freeBooksLimit: yup
    .number()
    .typeError("Free Books Limit must be a number")
    .required("Free Books Limit is required"),
  premiumBooksLimit: yup
    .number()
    .typeError("Premium Books Limit must be a number")
    .required("Premium Books Limit is required"),
  platformFee: yup
    .number()
    .typeError("Platform Fee must be a number")
    .required("Platform Fee is required"),
  signature: yup.string().required("Signature is required"),
});

const dateSchema = yup.object({
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().required("End date is required"),
  croppedImg: yup.string().required("Cropped Image is required"),
});
const renewFeatureSchema = yup.object({
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().required("End date is required"),
});

const uploadReceiptSchema = yup.object({
  receipt_image: yup.string().required("Receipt image is required"),
  payment_date: yup
    .string()
    .required("Payment date is required")
    .test(
      "not-in-future",
      "Future date is not allowed",
      (value) => {
        if (!value) return true;
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return new Date(value) <= endOfToday;
      }
    ),
  transaction_id: yup.string().required("Transaction id is required"),
});

const customNotificationValidation = yup.object({
  role: yup
    .array()
    .min(1, "Select at least one role to continue.")
    .required("Role is required."),
  method: yup
    .array()
    .min(1, "Select at least one type to continue.")
    .required("Notification type is required"),
  direction: yup.mixed().nullable().optional(),
  selectedItem: yup.object().required("Select item to continue"),
  title: yup.string().required("Title is required"),
  body: yup.string().required("Description is required"),
});

const announcementBannerValidation = yup.object({
  banner_image: yup.string().required("Banner image is required"),
  title: yup.string().required("Title is required"),
  user_type: yup.string().required("User type is required"),
  is_clickable: yup.string().optional(),
  button_image: yup.string().optional(),
});

const createSchoolPlanSchema = yup.object().shape({
  students_count: yup
    .number()
    .typeError("Must be a valid number")
    .required("Students count is required")
    .min(0, "Cannot be negative"),
  teachers_count: yup
    .number()
    .typeError("Must be a valid number")
    .required("Teachers count is required")
    .min(0, "Cannot be negative"),
  is_multi_campus: yup
    .string()
    .required("Multi campus selection is required"),
  duration: yup
    .number()
    .typeError("Must be a valid number")
    .required("Duration is required")
    .min(1, "Minimum duration is 1 day")
    .max(365, "Maximum duration is 365 days"),
  campuses_count: yup.number().when("is_multi_campus", {
    is: "1",
    then: (schema) =>
      schema
        .typeError("Must be a valid number")
        .required("Number of campuses is required")
        .min(1, "Must be at least 1"),
    otherwise: (schema) => schema.optional(),
  }),
});

export {
  loginSchema,
  createTicketSchema,
  createCouponSchema,
  updateCouponSchema,
  createBookReviewerSchema,
  editProfileSchema,
  changePasswordSchema,
  getEmailSchema,
  MOUSchema,
  dateSchema,
  renewFeatureSchema,
  mouSignatureSchema,
  uploadReceiptSchema,
  customNotificationValidation,
  announcementBannerValidation,
  createSchoolPlanSchema,
};
